<!-- components/Button.svelte -->
<script>
  export let href = null;
  export let variant = "primary"; // primary, secondary, ghost
  export let size = "medium"; // small, medium, large, compact
  export let disabled = false;
  export let type = "button";
  export let reCAPTCHA = false;
  export let dataCallback = undefined;
  export let responsive = false;
  export let fullWidth = false;
  export let noWrap = false;
  export let className = "";

  // reCAPTCHA v3 site key: populated from rollup.config.js based on environment
  const reCAPTCHA_site_key = "RECAPTCHA_SITE_KEY";

  $: classes =
    `btn btn--${variant} btn--${size} ${reCAPTCHA ? "g-recaptcha" : ""} ${responsive ? "btn--responsive" : ""} ${fullWidth ? "btn--full-width" : ""} ${noWrap ? "btn--no-wrap" : ""} ${className}`.trim();

  // Set up global callback for reCAPTCHA
  if (typeof window !== "undefined" && dataCallback) {
    window.onDataCallback = (token) => {
      dataCallback(token);
    };
  }
</script>

{#if href}
  <a {href} class={classes}>
    <slot />
  </a>
{:else}
  <button
    class={classes}
    {disabled}
    {type}
    data-sitekey={reCAPTCHA ? reCAPTCHA_site_key : ""}
    data-callback={reCAPTCHA ? "onDataCallback" : ""}
    data-action={reCAPTCHA ? "submit" : ""}
    on:click
  >
    <slot />
  </button>
{/if}

<style lang="scss">
  .btn {
    font-weight: 700;
    border-radius: 8px;
    text-decoration: none;
    display: inline-block;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    border: 1px solid transparent;
    cursor: pointer;
    background: none;
    text-align: center;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      transition: left 0.5s ease;
      z-index: 1;
    }

    &:hover::before {
      left: 100%;
    }

    &:not(:hover)::before {
      left: -100%;
      transition: left 0.3s ease;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;

      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }

  /* Variants */
  .btn--primary {
    background: linear-gradient(
      135deg,
      var(--text-color1) 0%,
      var(--text-color1) 100%
    );
    color: #1e0f30;

    &:hover {
      color: #1e0f30;
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(210, 165, 82, 0.4);
      border-color: rgba(255, 255, 255, 0.2);
      background: linear-gradient(135deg, var(--text-color1) 0%, #f0d76e 100%);
    }
  }

  .btn--secondary {
    background: transparent;
    color: var(--text-color1);
    border: 2px solid var(--text-color1);

    &:hover {
      background: var(--text-color1);
      color: #1e0f30;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(210, 165, 82, 0.3);
    }
  }

  .btn--ghost {
    background: transparent;
    color: var(--text-color3);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
      transform: translateY(-1px);
    }
  }

  /* Sizes */
  .btn--small {
    font-size: 0.9rem;
    padding: 0.75rem 1.5rem;
  }

  .btn--medium {
    font-size: 1rem;
    padding: 1rem 2rem;
  }

  .btn--large {
    font-size: 1.1rem;
    padding: 1.2rem 2.5rem;
  }

  .btn--compact {
    font-size: 0.95rem;
    padding: 0.75rem 1.5rem;
  }

  .btn--full-width {
    width: 100%;
  }

  .btn--no-wrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .btn--responsive {
    @media (max-width: 480px) {
      font-size: 0.875rem;
      padding: 0.75rem 1rem;
      min-height: auto;
    }

    @media (max-width: 380px) {
      font-size: 0.8rem;
      padding: 0.625rem 0.75rem;
    }

    @media (max-width: 340px) {
      font-size: 0.75rem;
      padding: 0.5rem 0.5rem;
    }

    @media (max-width: 320px) {
      font-size: 0.7rem;
      padding: 0.4rem 0.4rem;
    }

    @media (max-width: 300px) {
      font-size: 0.65rem;
      padding: 0.35rem 0.3rem;
    }
  }

  /* Hide reCAPTCHA badge entirely to prevent mobile overlay issues */
  :global(.grecaptcha-badge) {
    visibility: hidden !important;
  }

  /* Alternative: If you want to keep the badge but position it better
  :global(.grecaptcha-badge) {
    position: fixed !important;
    bottom: 14px !important;
    right: 14px !important;
    z-index: 1000 !important;
    transition: opacity 0.3s ease !important;
  }

  @media (max-width: 480px) {
    :global(.grecaptcha-badge) {
      bottom: 10px !important;
      right: 10px !important;
      transform: scale(0.8) !important;
    }
  }
  */
</style>
